import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  communityFollowers,
  communityTopFans,
  communityVip,
  communityEmbajadores,
  communityHosts,
  communityPrograms,
  communitySections,
  communityKpis,
  type CommunityUser,
  type CommunityHost,
  type CommunityProgram,
} from '@/mocks/adminCommunity';
import UserCard from './components/UserCard';
import HostCard from './components/HostCard';
import ProgramCard from './components/ProgramCard';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';

export default function AdminCommunityPage() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [search, setSearch] = useState('');

  const loadData = useCallback(() => {
    setDataState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.05;
      const shouldBeEmpty = Math.random() < 0.03;
      if (shouldError) {
        setDataState('error');
      } else if (shouldBeEmpty) {
        setDataState('empty');
      } else {
        setDataState('ready');
      }
    }, 700);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const retry = () => loadData();

  /* ─── Filter by search ─── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return {
      seguidores: communityFollowers,
      topfans: communityTopFans,
      vip: communityVip,
      embajadores: communityEmbajadores,
      locutores: communityHosts,
      programas: communityPrograms,
    };
    return {
      seguidores: communityFollowers.filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)),
      topfans: communityTopFans.filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)),
      vip: communityVip.filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)),
      embajadores: communityEmbajadores.filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)),
      locutores: communityHosts.filter((h) => h.name.toLowerCase().includes(q) || h.programName.toLowerCase().includes(q)),
      programas: communityPrograms.filter((p) => p.name.toLowerCase().includes(q) || p.hostName.toLowerCase().includes(q)),
    };
  }, [search]);

  const totalResults =
    filtered.seguidores.length + filtered.topfans.length + filtered.vip.length +
    filtered.embajadores.length + filtered.locutores.length + filtered.programas.length;

  /* ─── Handlers ─── */
  const handleUserClick = async (user: CommunityUser) => {
    const tierLabel = user.tier === 'topfan' ? 'Top Fan' : user.tier === 'vip' ? 'VIP' : user.tier === 'embajador' ? 'Embajador' : 'Seguidor';
    await Swal.fire({
      title: user.name,
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p><strong>Usuario:</strong> ${user.username}</p>
          <p><strong>País:</strong> ${user.countryFlag} ${user.country}</p>
          <p><strong>Nivel:</strong> ${tierLabel}</p>
          <p><strong>Interacciones:</strong> ${user.interactions}</p>
          <p><strong>Última actividad:</strong> ${new Date(user.lastActive).toLocaleString('es-CR')}</p>
          <p><strong>Se unió:</strong> ${new Date(user.joinedAt).toLocaleDateString('es-CR', { year: 'numeric', month: 'long' })}</p>
          ${user.bio ? `<p style="margin-top:8px;color:#6b7280;"><em>"${user.bio}"</em></p>` : ''}
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#6b7280',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    });
  };

  const handleHostClick = async (host: CommunityHost) => {
    await Swal.fire({
      title: host.name,
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p><strong>Usuario:</strong> ${host.username}</p>
          <p><strong>Programa:</strong> ${host.programName}</p>
          <p><strong>Horario:</strong> ${host.programSchedule}</p>
          <p><strong>Seguidores:</strong> ${host.followers.toLocaleString()}</p>
          <p><strong>Programas:</strong> ${host.totalPrograms}</p>
          <p><strong>Estado:</strong> ${host.isLive ? '🔴 En vivo' : host.isActive ? '🟢 Activo' : '⚫ Inactivo'}</p>
          ${host.bio ? `<p style="margin-top:8px;color:#6b7280;"><em>"${host.bio}"</em></p>` : ''}
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#6b7280',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    });
  };

  const handleProgramClick = async (program: CommunityProgram) => {
    await Swal.fire({
      title: program.name,
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p><strong>Anfitrión:</strong> ${program.hostName}</p>
          <p><strong>Horario:</strong> ${program.schedule}</p>
          <p><strong>Categoría:</strong> ${program.category}</p>
          <p><strong>Participantes:</strong> ${program.participants}</p>
          <p style="margin-top:8px;color:#6b7280;">${program.description}</p>
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#6b7280',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    });
  };

  const handleSearchChange = (value: string) => setSearch(value);

  /* ════════════════════════════════════════════
     LOADING
     ════════════════════════════════════════════ */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-48 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-3 animate-pulse">
                    <div className="w-8 h-8 rounded-lg bg-background-200 mb-2"></div>
                    <div className="w-12 h-4 rounded bg-background-200 mb-1"></div>
                    <div className="w-16 h-2.5 rounded bg-background-200"></div>
                  </div>
                ))}
              </div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-3">
                  <div className="w-36 h-5 rounded-md bg-background-200 animate-pulse"></div>
                  <div className="flex gap-3 overflow-hidden">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="flex-shrink-0 w-[260px] h-40 rounded-2xl bg-background-50 border border-background-200 animate-pulse"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     ERROR
     ════════════════════════════════════════════ */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-error-warning-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar la comunidad</h3>
              <p className="text-foreground-500 text-sm mb-5">No se pudo cargar la información de la comunidad.</p>
              <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-refresh-line"></i> Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     EMPTY
     ════════════════════════════════════════════ */
  if (dataState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-group-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-group-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Comunidad vacía</h3>
              <p className="text-foreground-500 text-sm mb-5">Tu radio aún no tiene comunidad. Cuando los oyentes empiecen a seguirte e interactuar, aparecerán acá.</p>
            </div>
          </main>
        </div>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
          <div className="flex items-center justify-around h-[56px] px-1">
            {[
              { icon: 'ri-dashboard-line', activeIcon: 'ri-dashboard-fill', label: 'Dashboard', active: false, href: '/admin/dashboard' },
              { icon: 'ri-calendar-2-line', activeIcon: 'ri-calendar-2-fill', label: 'Programación', active: false, href: '/admin/schedule' },
              { icon: 'ri-group-line', activeIcon: 'ri-group-fill', label: 'Comunidad', active: true, href: '/admin/community' },
              { icon: 'ri-broadcast-line', activeIcon: 'ri-broadcast-fill', label: 'Cabina', active: false, href: '/admin/cabina' },
              { icon: 'ri-settings-3-line', activeIcon: 'ri-settings-3-fill', label: 'Ajustes', active: false, href: '/admin/settings' },
            ].map((item) => (
              <Link key={item.label} to={item.href} className={`flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer transition-colors relative ${item.active ? 'text-primary-600' : 'text-foreground-400'}`}>
                <i className={`${item.active ? item.activeIcon : item.icon} text-xl`}></i>
                <span className="text-[10px] font-semibold">{item.label}</span>
                {item.active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-500 rounded-full"></span>}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     READY — Single View Community Center
     ════════════════════════════════════════════ */

  /* ─── Section data map ─── */
  const sectionData: Record<string, { users?: CommunityUser[]; hosts?: CommunityHost[]; programs?: CommunityProgram[] }> = {
    seguidores:  { users: filtered.seguidores },
    topfans:     { users: filtered.topfans },
    vip:         { users: filtered.vip },
    embajadores: { users: filtered.embajadores },
    locutores:   { hosts: filtered.locutores },
    programas:   { programs: filtered.programas },
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-group-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Centro de Comunidad</h1>
              <p className="text-foreground-400 text-[9px]">{communityKpis.totalSeguidores} seguidores</p>
            </div>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Centro de Comunidad</h2>
              <p className="text-foreground-400 text-xs">
                Construí tu comunidad. {communityKpis.totalSeguidores} seguidores · {communityKpis.totalActivos} activos · {communityKpis.totalVip} VIP · {communityKpis.totalEmbajadores} embajadores.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/dashboard" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-dashboard-line text-xs"></i> Dashboard
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-6">
            {/* ═══ KPI Row ═══ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {communitySections.map((section) => (
                <a
                  key={section.id}
                  href={`#section-${section.id}`}
                  className={`rounded-2xl border border-background-200 ${section.bgColor} p-3 cursor-pointer hover:scale-[1.03] transition-transform duration-200`}
                >
                  <div className={`w-9 h-9 rounded-xl ${section.bgColor} flex items-center justify-center mb-2`}>
                    <i className={`${section.icon} ${section.color} text-base`}></i>
                  </div>
                  <p className="text-foreground-950 text-xl font-bold">{section.count}</p>
                  <p className="text-foreground-400 text-[11px] font-medium">{section.title}</p>
                </a>
              ))}
            </div>

            {/* ═══ Interacciones totales ═══ */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-flashlight-line text-accent-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-foreground-950 text-2xl font-bold">{communityKpis.totalInteracciones.toLocaleString()}</p>
                  <p className="text-foreground-400 text-xs">Interacciones totales de la comunidad</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {communityKpis.totalActivos} activos ahora
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
                  <i className="ri-vip-diamond-fill text-[11px]"></i>
                  {communityKpis.totalVip} VIP
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                  <i className="ri-shield-star-line text-[11px]"></i>
                  {communityKpis.totalEmbajadores} embajadores
                </span>
              </div>
            </div>

            {/* ═══ Search bar ═══ */}
            <div className="relative max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Buscar personas, locutores o programas..."
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-background-200 bg-background-50 text-foreground-700 text-xs outline-none focus:border-primary-400 transition-colors"
              />
              {search && (
                <>
                  <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer text-foreground-400 hover:text-foreground-600">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                  <span className="absolute -bottom-5 left-0 text-foreground-400 text-[10px]">{totalResults} resultados</span>
                </>
              )}
            </div>
            {search && <div className="h-1"></div>}

            {/* ═══ 6 Sections ═══ */}
            <div className="space-y-8">
              {communitySections.map((section) => {
                const data = sectionData[section.id];
                const users = data?.users;
                const hosts = data?.hosts;
                const programs = data?.programs;
                const hasData = (users && users.length > 0) || (hosts && hosts.length > 0) || (programs && programs.length > 0);

                if (!hasData) return null;

                return (
                  <section key={section.id} id={`section-${section.id}`} className="space-y-3">
                    {/* Section header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${section.bgColor} flex items-center justify-center`}>
                          <i className={`${section.icon} ${section.color} text-sm`}></i>
                        </div>
                        <div>
                          <h3 className="text-foreground-950 font-bold text-sm">{section.title}</h3>
                          <p className="text-foreground-400 text-[10px] hidden sm:block">{section.description}</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${section.bgColor} ${section.color} text-[10px] font-semibold`}>
                          {users ? users.length : hosts ? hosts.length : programs ? programs.length : 0}
                        </span>
                      </div>
                    </div>

                    {/* Horizontal scrollable card row */}
                    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
                      {users && users.map((user) => (
                        <div key={user.id} onClick={() => handleUserClick(user)} className="cursor-pointer">
                          <UserCard user={user} showRank={section.id === 'topfans'} />
                        </div>
                      ))}
                      {hosts && hosts.map((host) => (
                        <div key={host.id} onClick={() => handleHostClick(host)} className="cursor-pointer">
                          <HostCard host={host} />
                        </div>
                      ))}
                      {programs && programs.map((program) => (
                        <div key={program.id} onClick={() => handleProgramClick(program)} className="cursor-pointer">
                          <ProgramCard program={program} />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            {/* ═══ All empty after search ═══ */}
            {search && totalResults === 0 && (
              <div className="bg-background-50 rounded-2xl border border-background-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-foreground-950 font-bold text-base mb-1">Sin resultados</h3>
                <p className="text-foreground-500 text-sm mb-4">No encontramos nada para "{search}". Probá con otro término.</p>
                <button onClick={() => setSearch('')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-filter-off-line text-sm"></i> Limpiar búsqueda
                </button>
              </div>
            )}
          </div>
        </main>
      </div>


    </div>
  );
}